import React, { useEffect, useMemo, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine, CartesianGrid } from "recharts";
import { Activity, Plus, X, Bluetooth, Sparkles, AlertTriangle, ChevronDown, TrendingUp, TrendingDown, Watch } from "lucide-react";
import api from "../lib/api";
import { FullScreenLoader, PageHeader, EmptyState } from "../components/ui-kit";

const VITAL_TYPES = [
  { key: "bp_systolic", label: "BP Systolic", unit: "mmHg", color: "#F4844B" },
  { key: "bp_diastolic", label: "BP Diastolic", unit: "mmHg", color: "#EF4444" },
  { key: "pulse", label: "Pulse", unit: "bpm", color: "#0A4C36" },
  { key: "spo2", label: "SpO₂", unit: "%", color: "#0EA5E9" },
  { key: "temperature", label: "Temperature", unit: "°C", color: "#F59E0B" },
  { key: "weight", label: "Weight", unit: "kg", color: "#8B5CF6" },
  { key: "blood_glucose", label: "Blood Glucose", unit: "mg/dL", color: "#EC4899" },
  { key: "steps", label: "Steps", unit: "steps", color: "#10B981" },
  { key: "sleep_hours", label: "Sleep", unit: "hrs", color: "#6366F1" },
];

const RANGES = ["1D","3D","1W","2W","3W","1M","3M","6M","1Y","1Y+"];

const Vitals = () => {
  const [selected, setSelected] = useState(VITAL_TYPES[0].key);
  const [range, setRange] = useState("1W");
  const [series, setSeries] = useState([]);
  const [ranges, setRanges] = useState({});
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [alertSending, setAlertSending] = useState(false);
  const [alertSent, setAlertSent] = useState(false);

  const meta = VITAL_TYPES.find((v) => v.key === selected);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/patient/vitals", { params: { type: selected, range } }),
        api.get("/patient/vitals/anomalies"),
      ]);
      setSeries(s.data?.vitals || []);
      setRanges(s.data?.ranges || {});
      setAnomalies(a.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [selected, range]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = useMemo(() => {
    return series
      .slice()
      .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
      .map((v) => ({
        t: new Date(v.recorded_at).getTime(),
        value: Number(v.value),
        label: new Date(v.recorded_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
      }));
  }, [series]);

  const latest = chartData[chartData.length - 1];
  const previous = chartData[chartData.length - 2];
  const trend = latest && previous ? latest.value - previous.value : 0;

  const normalRange = ranges?.[selected];

  const addReading = async (payload) => {
    await api.post("/patient/vitals", payload);
    setAddOpen(false);
    load();
  };

  const mockConnect = async () => {
    setConnectOpen(false);
    // Generate a few sample values
    const now = new Date();
    const samples = [72, 74, 78, 80, 76];
    for (let i = 0; i < samples.length; i++) {
      const t = new Date(now.getTime() - (samples.length - i) * 3600 * 1000);
      await api.post("/patient/vitals", {
        type: "pulse", value: samples[i], unit: "bpm", recorded_at: t.toISOString(), source: "smartwatch_demo",
      });
    }
    setSelected("pulse");
    load();
  };

  const alertCircle = async () => {
    setAlertSending(true);
    try {
      await api.post("/patient/vitals/alert-circle", { vital_type: selected, message: `Anomalies in my ${meta?.label || selected} readings` });
      setAlertSent(true);
      setTimeout(() => setAlertSent(false), 4000);
    } finally { setAlertSending(false); }
  };

  return (
    <div className="pb-28">
      <PageHeader
        title="Vitals"
        subtitle="Track & trend your health"
        right={
          <div className="flex gap-2">
            <button data-testid="vitals-connect" onClick={() => setConnectOpen(true)} className="w-10 h-10 rounded-2xl bg-white border border-[#0A4C36]/10 text-[#0A4C36] flex items-center justify-center">
              <Bluetooth size={18} />
            </button>
            <button data-testid="vitals-add" onClick={() => setAddOpen(true)} className="w-10 h-10 rounded-2xl bg-[#F4844B] text-white flex items-center justify-center shadow-md shadow-[#F4844B]/25">
              <Plus size={20} />
            </button>
          </div>
        }
      />

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div className="px-5 mb-4">
          <div className="rounded-3xl p-4 bg-gradient-to-br from-red-50 to-orange-50 border border-red-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl bg-red-500 text-white flex items-center justify-center">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <div className="font-instrument font-semibold text-[#1C1917]">Anomalies detected</div>
                <div className="text-xs text-[#57534E]">Latest out-of-range readings in last 30 days</div>
              </div>
            </div>
            <div className="space-y-2 mb-3">
              {anomalies.slice(0, 3).map((an, i) => (
                <div key={i} data-testid={`anomaly-${an.type}`} className="flex items-center justify-between bg-white/70 rounded-xl px-3 py-2">
                  <div>
                    <div className="text-xs text-[#78716C]">{VITAL_TYPES.find((v) => v.key === an.type)?.label || an.type}</div>
                    <div className="font-instrument font-semibold text-red-500">{an.value} {an.unit}</div>
                  </div>
                  <div className="text-xs text-[#78716C]">{new Date(an.recorded_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
            <button
              data-testid="alert-my-circle"
              disabled={alertSending || alertSent}
              onClick={alertCircle}
              className="w-full py-3 rounded-2xl bg-red-500 text-white font-medium disabled:opacity-60"
            >
              {alertSent ? "✓ Circle alerted" : alertSending ? "Alerting…" : "Alert my Circle"}
            </button>
          </div>
        </div>
      )}

      {/* Vital type chips */}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar px-5 pb-3">
        {VITAL_TYPES.map((v) => (
          <button
            key={v.key}
            data-testid={`vital-chip-${v.key}`}
            onClick={() => setSelected(v.key)}
            className={`px-4 py-2 rounded-2xl text-sm font-medium whitespace-nowrap transition-all ${
              selected === v.key ? "bg-[#0A4C36] text-white" : "bg-white text-[#57534E] border border-[#0A4C36]/10"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Chart card */}
      <div className="px-5">
        <div className="card !p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs text-[#78716C]">Latest</div>
              <div className="flex items-baseline gap-2">
                <div className="font-instrument text-4xl font-semibold text-[#0A4C36]" data-testid="vital-latest">
                  {latest ? latest.value : "—"}
                </div>
                <div className="text-sm text-[#78716C]">{meta?.unit}</div>
                {previous && (
                  <div className={`flex items-center gap-0.5 text-xs font-medium ${trend > 0 ? "text-red-500" : "text-emerald-600"}`}>
                    {trend > 0 ? <TrendingUp size={12} /> : trend < 0 ? <TrendingDown size={12} /> : null}
                    {trend !== 0 && <span>{Math.abs(trend).toFixed(1)}</span>}
                  </div>
                )}
              </div>
              {normalRange && (
                <div className="text-xs text-[#78716C] mt-1">
                  Normal: <span className="font-medium text-[#0A4C36]">{normalRange.low}–{normalRange.high} {normalRange.unit || meta?.unit}</span>
                </div>
              )}
            </div>
          </div>

          <div className="h-[180px] w-full -ml-2" style={{ minHeight: 180 }}>
            {loading ? <div className="h-full flex items-center justify-center text-xs text-[#78716C]">Loading…</div>
              : chartData.length === 0 ? <div className="h-full flex items-center justify-center text-xs text-[#78716C]">No data for this range</div>
              : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={meta?.color} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={meta?.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#0A4C3610" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#78716C" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "#78716C" }} axisLine={false} tickLine={false} domain={["auto", "auto"]} width={30} />
                  <Tooltip
                    contentStyle={{ background: "#0A4C36", border: "none", borderRadius: 12, color: "#fff", fontSize: 12 }}
                    labelStyle={{ color: "#FCD9C8" }}
                    formatter={(v) => [`${v} ${meta?.unit}`, meta?.label]}
                  />
                  {normalRange && (
                    <>
                      <ReferenceLine y={normalRange.low} stroke="#10B98155" strokeDasharray="3 3" />
                      <ReferenceLine y={normalRange.high} stroke="#EF444455" strokeDasharray="3 3" />
                    </>
                  )}
                  <Area type="monotone" dataKey="value" stroke={meta?.color} strokeWidth={2.5} fill="url(#areaGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Range selector */}
          <div className="flex gap-1.5 overflow-x-auto hide-scrollbar mt-3 pt-3 border-t border-[#0A4C36]/5">
            {RANGES.map((r) => (
              <button
                key={r}
                data-testid={`range-${r}`}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  range === r ? "bg-[#0A4C36] text-white" : "text-[#57534E] hover:bg-[#E6F0EC]"
                }`}
              >{r}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Empty */}
      {chartData.length === 0 && !loading && (
        <EmptyState
          icon={Activity}
          title="No readings yet"
          subtitle="Tap the + button to log your first reading"
          action={<button onClick={() => setAddOpen(true)} className="btn-primary max-w-[220px]">Add reading</button>}
        />
      )}

      {/* Add sheet */}
      {addOpen && (
        <AddVitalSheet onClose={() => setAddOpen(false)} onSave={addReading} defaultType={selected} />
      )}
      {connectOpen && (
        <ConnectSheet onClose={() => setConnectOpen(false)} onConnect={mockConnect} />
      )}
    </div>
  );
};

const AddVitalSheet = ({ onClose, onSave, defaultType }) => {
  const [type, setType] = useState(defaultType);
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = VITAL_TYPES.find((v) => v.key === type);
  const submit = async () => {
    if (!value) return;
    setBusy(true);
    try {
      await onSave({ type, value: Number(value), unit: meta?.unit, notes });
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="add-vital-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-[430px] bg-white rounded-t-[32px] p-5 pb-8">
        <div className="w-12 h-1.5 rounded-full bg-[#E7E5E4] mx-auto mb-4" />
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-instrument text-xl font-semibold text-[#0A4C36]">Log new reading</h3>
          <button onClick={onClose} data-testid="add-vital-close"><X size={20} className="text-[#78716C]" /></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-[#57534E] ml-1">Vital type</span>
            <div className="relative mt-1">
              <select data-testid="add-vital-type" className="input-field appearance-none pr-10" value={type} onChange={(e) => setType(e.target.value)}>
                {VITAL_TYPES.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
              </select>
              <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#78716C] pointer-events-none" />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#57534E] ml-1">Value ({meta?.unit})</span>
            <input data-testid="add-vital-value" type="number" step="0.1" className="input-field mt-1" value={value} onChange={(e) => setValue(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#57534E] ml-1">Notes (optional)</span>
            <input data-testid="add-vital-notes" className="input-field mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <button data-testid="add-vital-save" onClick={submit} disabled={busy || !value} className="btn-primary mt-2">
            {busy ? "Saving…" : "Save reading"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConnectSheet = ({ onClose, onConnect }) => (
  <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="connect-sheet">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className="relative w-full max-w-[430px] bg-white rounded-t-[32px] p-6 pb-8 text-center">
      <div className="w-12 h-1.5 rounded-full bg-[#E7E5E4] mx-auto mb-5" />
      <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center mx-auto mb-4 animate-float">
        <Watch size={30} className="text-white" />
      </div>
      <h3 className="font-instrument text-xl font-semibold text-[#0A4C36] mb-1">Connect a device</h3>
      <p className="text-sm text-[#57534E] mb-4">
        Sync your smartwatch, fitness band, or phone health app. Coming soon: Apple Health, Google Fit, Fitbit.
      </p>
      <button data-testid="mock-connect-device" onClick={onConnect} className="btn-primary flex items-center justify-center gap-2">
        <Sparkles size={16} /> Try demo sync
      </button>
      <button onClick={onClose} data-testid="connect-close" className="w-full py-3 mt-2 text-[#57534E] font-medium">Close</button>
    </div>
  </div>
);

export default Vitals;
